import { App } from '@capacitor/app';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';

import { shouldRetryQuery } from '@/core/lib/list-pagination';
import { getApiErrorStatus } from '@/core/utils/api-error.utils';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

function isFailedClientError(query: {
  state: { status: string; error: unknown };
}): boolean {
  if (query.state.status !== 'error') return false;
  const status = getApiErrorStatus(query.state.error);
  return status != null && status >= 400 && status < 500 && status !== 408;
}

/**
 * Capacitor resume does not fire `visibilitychange` / window focus, so React
 * Query's `refetchOnWindowFocus` never runs. Mirror that policy for stale
 * active queries when the native app becomes active again.
 */
function NativeResumeQueryRefetch() {
  const client = useQueryClient();

  useEffect(() => {
    if (!isCapacitorNative()) return;

    let cancelled = false;
    let listener: Awaited<ReturnType<typeof App.addListener>> | null = null;

    void App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      void client.refetchQueries({
        type: 'active',
        stale: true,
        predicate: (query) => !isFailedClientError(query),
      });
    }).then((handle) => {
      if (cancelled) {
        void handle.remove();
        return;
      }
      listener = handle;
    });

    return () => {
      cancelled = true;
      if (listener) {
        void listener.remove();
      }
    };
  }, [client]);

  return null;
}

// Create a client with caching strategy
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds - data considered fresh for 30s
      gcTime: 5 * 60 * 1000, // 5 minutes - cache kept for 5min (formerly cacheTime)
      refetchOnWindowFocus: (query) => !isFailedClientError(query),
      refetchOnReconnect: (query) => !isFailedClientError(query),
      refetchOnMount: (query) => {
        // Remounts (Ionic tab stacks, Strict Mode) must not re-flood a known 4xx.
        if (isFailedClientError(query)) return false;
        return true;
      },
      retry: shouldRetryQuery,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8_000),
    },
    mutations: {
      retry: false,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <NativeResumeQueryRefetch />
      {children}
    </QueryClientProvider>
  );
}

// Export queryClient for use in hooks/mutations
export { queryClient };
